// Bilingual content (IT default, EN). Structured by page/section.
export const translations = {
  it: {
    nav: {
      platform: "Piattaforma",
      useCases: "Casi d'uso",
      industries: "Settori",
      company: "Azienda",
      pilot: "Programma Pilota",
      investors: "Investitori",
      contact: "Contatti",
      cta: "Richiedi una sessione di discovery",
    },
    common: {
      applyPilot: "Candidati come partner pilota",
      discoverPlatform: "Scopri la piattaforma",
      viewDemo: "Esplora la simulazione interattiva",
      talkUseCase: "Parliamo del tuo caso d'uso",
      inDevelopment: "Dynamis è in fase di sviluppo",
      selectingPartners: "Stiamo selezionando partner pilota",
    },
    home: {
      metaTitle: "Dynamis — Supply Chain Decision Intelligence",
      metaDesc: "Dynamis connette dati, sistemi e vincoli operativi, simula scenari alternativi e aiuta il management a individuare l'azione più efficace.",
      eyebrow: "Supply Chain Decision Intelligence",
      title: "Trasforma i dati della supply chain in decisioni migliori.",
      subtitle: "Dynamis connette dati, sistemi e vincoli operativi, simula scenari alternativi e aiuta il management a individuare l'azione più efficace prima che un problema diventi un costo.",
      problemTitle: "I dati esistono. Le decisioni restano frammentate.",
      problemBody: "Le aziende dispongono di ERP, dashboard e grandi quantità di dati, ma molte decisioni critiche vengono ancora prese attraverso fogli Excel, analisi manuali, riunioni e informazioni non sincronizzate.",
      problems: [
        { t: "Dati distribuiti", d: "Informazioni distribuite tra sistemi e funzioni, difficili da collegare in un quadro coerente." },
        { t: "Strumenti di reporting", d: "Strumenti orientati al reporting, non alla scelta dell'azione da intraprendere." },
        { t: "Decisioni lente", d: "Decisioni cross-funzionali lente, difficili da spiegare e da misurare." },
      ],
      problemClose: "Il problema non è sapere soltanto cosa sta accadendo. È capire cosa conviene fare adesso.",
      cycleTitle: "Come funziona",
      cycleIntro: "Un ciclo continuo che porta dai dati grezzi alla decisione eseguita e misurata.",
      cycle: [
        { k: "Sense", d: "Integrare dati, eventi, vincoli e segnali provenienti dai sistemi aziendali in una rappresentazione unica e aggiornata." },
        { k: "Simulate", d: "Comprendere le cause di un problema e simulare scenari alternativi per anticiparne gli effetti sulla rete." },
        { k: "Solve", d: "Identificare l'alternativa migliore combinando intelligenza artificiale e ottimizzazione matematica." },
        { k: "Execute", d: "Approvare, tracciare e supportare l'esecuzione della decisione, mantenendo il controllo umano." },
      ],
      featuresTitle: "Una piattaforma, molte capacità.",
      featuresIntro: "Dynamis combina più tecnologie in un unico livello di intelligenza decisionale.",
      features: [
        { t: "Supply Chain Digital Twin", d: "Una rappresentazione dinamica e interrogabile dell'intera supply chain." },
        { t: "Data Integration Layer", d: "Connessione a ERP, WMS, TMS, MES, data lake, database e file Excel." },
        { t: "AI e sistemi multi-agente", d: "Agenti specializzati che collaborano su domanda, capacità, scorte e logistica." },
        { t: "Simulazioni what-if", d: "Confronto tra scenari alternativi prima di prendere una decisione." },
        { t: "Root-cause analysis", d: "Individuazione delle cause profonde di ritardi e inefficienze." },
        { t: "Ottimizzazione matematica", d: "Soluzioni ottimali rispetto a costi, servizio, capacità e rischio." },
        { t: "Assistente conversazionale", d: "Interrogazione della supply chain in linguaggio naturale." },
        { t: "Decision Board executive", d: "Una vista sintetica delle decisioni aperte e del loro impatto." },
        { t: "Human-in-the-Loop", d: "Le decisioni critiche restano sempre sotto supervisione umana." },
        { t: "Decision Memory", d: "Tracciabilità di decisioni, motivazioni, risultati e impatto nel tempo." },
      ],
      valueTitle: "Il valore che Dynamis punta a produrre.",
      valueIntro: "Risultati potenziali abilitati da decisioni più rapide, coordinate e misurabili.",
      values: [
        "Decisioni più rapide e coordinate tra funzioni",
        "Riduzione di costi logistici e operativi",
        "Migliore utilizzo della capacità produttiva",
        "Ottimizzazione di scorte e capitale circolante",
        "Miglioramento di SLA, OTIF e continuità operativa",
        "Identificazione anticipata di rischi e colli di bottiglia",
        "Maggiore trasparenza e tracciabilità delle decisioni",
      ],
      integrationTitle: "Sopra i sistemi esistenti, non al loro posto.",
      integrationBody: "Dynamis non sostituisce i sistemi aziendali. Opera come decision intelligence layer sopra di essi e ne trasforma i dati in decisioni coordinate.",
      externalSources: "Fonti esterne",
      network: {
        ariaLabel: "Rete di supply chain: un evento si propaga e Dynamis valuta un percorso alternativo",
        supplier: "Fornitore", factory: "Stabilimento", warehouse: "Magazzino",
        transport: "Trasporto", customer: "Cliente", event: "Evento rilevato",
        alternative: "Alternativa valutata", network: "Rete",
      },
      videoTitle: "Guarda Dynamis in azione",
      videoBody: "Una panoramica della piattaforma e del ciclo Sense → Simulate → Solve → Execute.",
      pilotTitle: "Costruiamo il primo caso d'uso sui dati reali della tua azienda.",
      pilotIntro: "Dynamis sta selezionando un numero limitato di aziende con supply chain complesse per sviluppare i primi progetti pilota.",
      industriesTitle: "Settori",
      industriesIntro: "Progettata per contesti industriali con reti di fornitura complesse.",
      closingTitle: "La prossima generazione di supply chain non si limiterà a osservare. Deciderà meglio.",
      closingBody: "Porta a Dynamis una decisione complessa, un processo frammentato o un caso d'uso ad alto impatto. Valuteremo insieme se esistono dati, condizioni e valore sufficienti per sviluppare un progetto pilota.",
    },
    platform: {
      metaTitle: "Piattaforma — Dynamis",
      metaDesc: "Architettura concettuale, digital twin, orchestrazione multi-agente, simulazioni, ottimizzazione, spiegabilità e Decision Memory.",
      eyebrow: "La piattaforma",
      title: "Un livello di intelligenza decisionale sopra la tua supply chain.",
      intro: "Dynamis costruisce una rappresentazione dinamica della supply chain e la usa per simulare, ottimizzare e supportare le decisioni operative e strategiche.",
      sections: [
        { t: "Architettura concettuale", d: "Un decision intelligence layer che si colloca sopra i sistemi transazionali e li orchestra, senza sostituirli." },
        { t: "Digital twin", d: "Una rappresentazione dinamica e interrogabile di stabilimenti, fornitori, magazzini, trasporti e clienti, con i relativi vincoli." },
        { t: "Orchestrazione multi-agente", d: "Agenti specializzati collaborano su domanda, capacità, scorte, logistica e rischi, coordinandosi verso obiettivi comuni." },
        { t: "Simulazioni what-if", d: "Scenari alternativi confrontati prima della decisione, per anticipare effetti e trade-off sulla rete." },
        { t: "Ottimizzazione matematica", d: "Soluzioni ottimali rispetto a costi, servizio, capacità, scorte e marginalità, nel rispetto dei vincoli." },
        { t: "Spiegabilità", d: "Raccomandazioni spiegabili: il management comprende perché un'alternativa è preferibile a un'altra." },
        { t: "Supervisione umana", d: "Le decisioni critiche restano sotto controllo umano. Dynamis supporta, non sostituisce il giudizio manageriale." },
        { t: "Decision Memory", d: "Registrazione di decisioni, motivazioni, risultati e impatto prodotto nel tempo, per apprendere e migliorare." },
        { t: "Integrazione progressiva", d: "Un perimetro dati circoscritto all'inizio, esteso per fasi successive man mano che il valore viene dimostrato." },
        { t: "Sicurezza e separazione dei dati", d: "Architettura progettata per la separazione dei dati tra clienti. Le misure di sicurezza sono in fase di definizione." },
      ],
      note: "Dynamis è in fase di sviluppo. Alcune funzionalità descritte sono in fase di rilascio progressivo.",
    },
    useCases: {
      metaTitle: "Casi d'uso — Dynamis",
      metaDesc: "Nove casi d'uso di decision intelligence per la supply chain: problema, decisione supportata e KPI potenzialmente influenzati.",
      eyebrow: "Casi d'uso",
      title: "Decisioni complesse, rese più rapide e misurabili.",
      intro: "Per ogni caso d'uso indichiamo il problema, la decisione supportata e i KPI potenzialmente influenzati.",
      labels: { problem: "Problema", decision: "Decisione supportata", kpi: "KPI potenzialmente influenzati" },
      items: [
        { t: "Decisioni di progettazione del prodotto", problem: "Alternative di prodotto, materiali e componenti devono essere valutate prima dell’industrializzazione.", decision: "Come confrontare configurazioni di prodotto considerando prestazioni, producibilità, costi e rischi di fornitura.", kpi: "Costo industriale, time-to-market, producibilità, rischio di fornitura" },
        { t: "Bilanciamento domanda, capacità e disponibilità", problem: "Domanda variabile a fronte di capacità e disponibilità limitate.", decision: "Come allineare piani di domanda, capacità e disponibilità nel breve e medio termine.", kpi: "Livello di servizio, saturazione capacità, costi di straordinario" },
        { t: "Pianificazione produttiva e gestione dei vincoli", problem: "Vincoli di produzione difficili da comporre manualmente.", decision: "Quale sequenza e allocazione produttiva rispetta i vincoli minimizzando i costi.", kpi: "OTIF, utilizzo impianti, costo unitario" },
        { t: "Ottimizzazione delle scorte", problem: "Scorte troppo alte in alcuni punti, rotture in altri.", decision: "Dove e quanto stoccare per bilanciare servizio e capitale circolante.", kpi: "Rotazione scorte, capitale immobilizzato, stock-out" },
        { t: "Allocazione di ordini e materiali", problem: "Materiali e ordini contesi tra più esigenze.", decision: "Come allocare ordini e materiali tra siti, linee e clienti.", kpi: "Fill rate, priorità cliente, margine" },
        { t: "Risposta a disruption e rischi dei fornitori", problem: "Eventi imprevisti che si propagano nella rete.", decision: "Quale azione di mitigazione adottare e con quale impatto.", kpi: "Continuità operativa, costo della disruption, tempo di risposta" },
        { t: "Ottimizzazione logistica e distributiva", problem: "Reti distributive complesse e costi di trasporto elevati.", decision: "Come instradare flussi e mezzi minimizzando costi e tempi.", kpi: "Costo di trasporto, lead time, saturazione mezzi" },
        { t: "Analisi delle cause di ritardi e inefficienze", problem: "Ritardi ricorrenti di cui non si conosce la causa.", decision: "Su quale leva intervenire per rimuovere la causa profonda.", kpi: "Puntualità, tempo di ciclo, rilavorazioni" },
        { t: "Valutazione dell'impatto economico di decisioni alternative", problem: "Difficoltà a confrontare economicamente le alternative.", decision: "Quale alternativa produce il miglior risultato economico complessivo.", kpi: "Margine, costo totale, ritorno atteso" },
      ],
    },
    industries: {
      metaTitle: "Settori — Dynamis",
      metaDesc: "Manufacturing, Consumer Goods & Retail, Pharma & Life Sciences, Food & Beverage, Fashion, Logistics & 3PL.",
      eyebrow: "Settori",
      title: "Pensata per supply chain industriali complesse.",
      intro: "Dynamis è progettata per contesti con molteplici vincoli, reti estese e decisioni ad alto impatto economico.",
      items: [
        { t: "Manufacturing", d: "Pianificazione produttiva, gestione dei vincoli e utilizzo della capacità." },
        { t: "Consumer Goods e Retail", d: "Bilanciamento tra domanda, scorte e livello di servizio su reti estese." },
        { t: "Pharma e Life Sciences", d: "Continuità di fornitura, tracciabilità e gestione dei vincoli regolatori." },
        { t: "Food & Beverage", d: "Deperibilità, stagionalità e ottimizzazione di scorte e logistica." },
        { t: "Fashion", d: "Cicli brevi, alta variabilità e allocazione di prodotti e materiali." },
        { t: "Logistics e 3PL", d: "Ottimizzazione di reti distributive, mezzi e flussi tra più clienti." },
      ],
    },
    company: {
      metaTitle: "Azienda — Dynamis",
      metaDesc: "La missione di Dynamis: rendere le decisioni complesse più rapide, misurabili, spiegabili e coerenti con gli obiettivi di business.",
      eyebrow: "Azienda",
      title: "Rendere le decisioni complesse migliori.",
      missionLabel: "La nostra missione",
      mission: "Rendere le decisioni complesse più rapide, misurabili, spiegabili e coerenti con gli obiettivi di business.",
      founderLabel: "Founder",
      founderName: "Silvio Ciabarra",
      founderRole: "Founder",
      linkedinLabel: "Profilo LinkedIn",
      founderBio: "Dirigente e transformation leader nella consulenza direzionale e tecnologica, nella gestione di programmi complessi e nella realizzazione di iniziative di trasformazione e innovazione per grandi organizzazioni.",
      teamNote: "Il team di Dynamis è in crescita. Nuove figure verranno presentate in questa sezione.",
    },
    pilot: {
      metaTitle: "Programma Pilota — Dynamis",
      metaDesc: "Un percorso strutturato in cinque fasi per costruire il primo caso d'uso sui dati reali della tua azienda.",
      eyebrow: "Programma Pilota",
      title: "Costruiamo il primo caso d'uso sui dati reali della tua azienda.",
      intro: "Dynamis sta selezionando un numero limitato di aziende con supply chain complesse. Il percorso è strutturato e a rischio contenuto.",
      steps: [
        { t: "Discovery", d: "Discovery e selezione del problema prioritario, ad alto impatto e con dati disponibili." },
        { t: "Business case", d: "Definizione del business case e dei KPI su cui misurare i risultati." },
        { t: "Dati", d: "Collegamento di un perimetro dati circoscritto, sufficiente al caso d'uso selezionato." },
        { t: "Proof of Concept", d: "Proof of Concept e simulazione degli scenari sulla base dei dati reali." },
        { t: "Misurazione", d: "Misurazione dei risultati e definizione di un piano di estensione per fasi." },
      ],
    },
    contact: {
      metaTitle: "Contatti — Dynamis",
      metaDesc: "Richiedi una sessione di discovery o candidati come partner pilota di Dynamis.",
      eyebrow: "Contatti",
      title: "Parliamo del tuo caso d'uso.",
      intro: "Compila il modulo: ti ricontatteremo per valutare insieme se esistono dati, condizioni e valore sufficienti per un progetto pilota.",
      fields: {
        fullName: "Nome e cognome",
        company: "Azienda",
        role: "Ruolo",
        email: "Email professionale",
        industry: "Settore",
        companySize: "Dimensione indicativa dell'azienda",
        message: "Problema o decisione che vuoi affrontare",
        interest: "Interesse",
        privacy: "Ho letto la Privacy Policy e acconsento al trattamento dei dati.",
      },
      placeholders: {
        select: "Seleziona…",
        message: "Descrivi brevemente la decisione o il processo su cui vuoi lavorare…",
      },
      interests: { discovery: "Sessione di discovery", demo: "Demo della piattaforma", pilot: "Programma pilota", partnership: "Partnership" },
      sizes: ["< 50 dipendenti", "50–250 dipendenti", "250–1.000 dipendenti", "1.000–5.000 dipendenti", "> 5.000 dipendenti"],
      submit: "Invia richiesta",
      sending: "Invio in corso…",
      successTitle: "Richiesta inviata",
      successBody: "Grazie. Abbiamo ricevuto la tua richiesta e ti ricontatteremo al più presto.",
      newRequest: "Invia un'altra richiesta",
      errors: {
        required: "Campo obbligatorio",
        email: "Inserisci un'email valida",
        privacy: "Il consenso privacy è obbligatorio",
        generic: "Si è verificato un errore. Riprova.",
      },
    },
    privacy: {
      metaTitle: "Privacy Policy — Dynamis",
      metaDesc: "Informativa sul trattamento dei dati personali di Dynamis Decision Intelligence S.r.l.",
      title: "Privacy Policy",
      updated: "Ultimo aggiornamento",
      body: [
        { t: "Titolare del trattamento", d: "Il titolare del trattamento è Dynamis Decision Intelligence S.r.l. Per qualsiasi richiesta è possibile scrivere a info@dynamisdecisionintelligence.com." },
        { t: "Dati raccolti", d: "Raccogliamo i dati che inserisci nel modulo di contatto: nome e cognome, azienda, ruolo, email professionale, settore, dimensione dell'azienda e il contenuto del messaggio." },
        { t: "Finalità", d: "I dati sono utilizzati esclusivamente per rispondere alle richieste, valutare l'idoneità a un progetto pilota e per le comunicazioni collegate." },
        { t: "Base giuridica", d: "Il trattamento si basa sul consenso e sull'interesse legittimo a dare seguito alle richieste ricevute." },
        { t: "Conservazione", d: "I dati sono conservati per il tempo necessario alle finalità indicate e nel rispetto degli obblighi di legge." },
        { t: "Diritti dell'interessato", d: "Puoi richiedere accesso, rettifica, cancellazione, limitazione e portabilità dei dati, oltre a opporti al trattamento, scrivendo a info@dynamisdecisionintelligence.com." },
        { t: "Cookie", d: "Questo sito utilizza esclusivamente cookie tecnici essenziali al funzionamento. Non vengono utilizzati cookie di profilazione né strumenti di tracciamento di terze parti." },
      ],
    },
    footer: {
      tagline: "Supply Chain Decision Intelligence.",
      company: "Azienda",
      product: "Prodotto",
      legal: "Legale",
      privacy: "Privacy Policy",
      rights: "Tutti i diritti riservati.",
      status: "Dynamis è in fase di sviluppo e sta selezionando partner pilota.",
    },
  },
};
